"use client";

import { useState } from "react";
import Link from "next/link";
import { ArrowRight, Search } from "@/components/icons";
import { InputGroup, InputGroupAddon, InputGroupInput } from "@/components/ui/input-group";

export interface DirectoryItem {
  title: string;
  href: string;
}

export interface DirectoryColumn {
  title: string;
  href: string;
  items: DirectoryItem[];
}

const COLLAPSED_ITEM_COUNT = 7;

function visibleItems(column: DirectoryColumn, filter: string): DirectoryItem[] {
  if (!filter) return column.items.slice(0, COLLAPSED_ITEM_COUNT);
  return column.items.filter((item) => item.title.toLowerCase().includes(filter));
}

function DirectoryColumnList({ column, filter }: { column: DirectoryColumn; filter: string }) {
  const items = visibleItems(column, filter);
  const hiddenCount = filter ? 0 : column.items.length - items.length;
  return (
    <div>
      <Link
        href={column.href}
        className="group flex items-end justify-between border-b-2 border-foreground pb-3"
      >
        <h3 className="font-semibold text-foreground group-hover:text-action">{column.title}</h3>
        <span className="tabular-nums text-2xl font-medium leading-none text-brand-strong">
          {String(column.items.length).padStart(2, "0")}
        </span>
      </Link>
      <ul>
        {items.map((item) => (
          <li key={item.href} className="border-b border-border">
            <Link
              href={item.href}
              className="group flex items-center justify-between gap-3 py-2.5 text-sm text-foreground hover:text-action"
            >
              {item.title}
              <ArrowRight className="size-3.5 shrink-0 text-fg-subtle group-hover:text-action" />
            </Link>
          </li>
        ))}
        {hiddenCount > 0 && (
          <li className="border-b border-border">
            <Link href={column.href} className="block py-2.5 text-sm font-medium text-action hover:underline">
              + {hiddenCount} more
            </Link>
          </li>
        )}
        {items.length === 0 && <li className="py-2.5 text-sm text-fg-subtle">No matches</li>}
      </ul>
    </div>
  );
}

export function SupportDirectoryList({ columns }: { columns: DirectoryColumn[] }) {
  const [filter, setFilter] = useState("");
  const normalizedFilter = filter.trim().toLowerCase();
  return (
    <section aria-labelledby="support-directory-heading" className="space-y-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <h2 id="support-directory-heading" className="text-2xl font-semibold tracking-tight text-foreground">
          Everything we have written down
        </h2>
        <InputGroup className="sm:w-72">
          <InputGroupAddon>
            <Search aria-hidden />
          </InputGroupAddon>
          <InputGroupInput
            type="search"
            value={filter}
            onChange={(event) => setFilter(event.target.value)}
            placeholder="Filter this list"
            aria-label="Filter the guides list"
          />
        </InputGroup>
      </div>
      <div className="grid gap-x-10 gap-y-10 sm:grid-cols-2 lg:grid-cols-4">
        {columns.map((column) => (
          <DirectoryColumnList key={column.title} column={column} filter={normalizedFilter} />
        ))}
      </div>
    </section>
  );
}
